
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MultiInterviewSearchPanel } from '@/components/search/MultiInterviewSearchPanel';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { LogOut, Users, Crown, Eye, Edit, Search, Shield } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface NavbarProps {
  sessionName: string;
  onSessionNameChange?: (name: string) => void;
  onOpenSharing?: () => void;
  showSharing?: boolean;
  userPermission?: 'view' | 'edit' | 'owner';
  homeButtonDestinationOverride? : string;
  showSearch?: boolean;
}

export const Navbar: React.FC<NavbarProps> = ({ 
  sessionName, 
  onSessionNameChange,
  onOpenSharing,
  showSharing = false,
  userPermission = 'owner',
  homeButtonDestinationOverride,
  showSearch = true,
}) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [localSessionName, setLocalSessionName] = useState(sessionName);

  // Update local state when sessionName prop changes
  useEffect(() => {
    setLocalSessionName(sessionName);
  }, [sessionName]);

  // Debounce the session name updates
  useEffect(() => {
    if (localSessionName === sessionName) return;
    
    const timeoutId = setTimeout(() => {
      if (onSessionNameChange) {
        onSessionNameChange(localSessionName);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [localSessionName, onSessionNameChange, sessionName]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  const handleGoHome = () => {
    if (homeButtonDestinationOverride) {
      navigate(homeButtonDestinationOverride);
    } else {
      navigate('/dashboard');
    }
  };

  const getPermissionIcon = () => {
    switch (userPermission) {
      case 'owner':
        return <Crown className="w-3 h-3" />;
      case 'edit':
        return <Edit className="w-3 h-3" />;
      case 'view':
        return <Eye className="w-3 h-3" />;
      default:
        return null;
    }
  };

  const getPermissionLabel = () => {
    switch (userPermission) {
      case 'owner':
        return 'Owner';
      case 'edit':
        return 'Editor';
      case 'view':
        return 'Viewer';
      default:
        return '';
    }
  };

  return (
    <nav className="bg-white border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4 pl-4 pr-4">
          <button
            onClick={handleGoHome}
            className="text-xl font-bold text-gray-900 hover:text-amber-600 transition-colors"
          >
            <div className='flex items-center'>
              <img className='w-16 h-16' src='/yak-stak-logo.png' alt='Yakky' />
              <div className="text-2xl font-bold text-gray-900 self-center">
                YakStak
              </div>
            </div>
          </button>
          
          <div className="h-6 w-px bg-gray-300" />
          
          <div className="flex items-center space-x-3">
            {onSessionNameChange ? (
              <Input
                value={localSessionName}
                onChange={(e) => setLocalSessionName(e.target.value)}
                className="text-lg font-medium border-none shadow-none p-0 h-auto bg-transparent focus-visible:ring-0"
                placeholder="Enter session name..."
              />
            ) : (
              <h1 className="text-lg font-medium text-gray-900">{sessionName}</h1>
            )}
            
            {showSharing && userPermission && (
              <Badge variant="secondary" className="flex items-center space-x-1">
                {getPermissionIcon()}
                <span className="text-xs">{getPermissionLabel()}</span>
              </Badge>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-3">
          {/* Multi-Interview Search */}
          {showSearch && <MultiInterviewSearchPanel 
            trigger={
              <Button 
                variant="outline" 
                size="sm" 
                className="flex items-center space-x-2 hover:bg-blue-50 hover:border-blue-300"
              >
                <Search className="w-4 h-4" />
                <span className="hidden md:inline">Search All</span>
              </Button>
            }
          />}
          
          {showSharing && onOpenSharing && (
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenSharing}
              className="flex items-center space-x-2"
            >
              <Users className="w-4 h-4" />
              <span>Share</span>
            </Button>
          )}
          
          {user && (
            <div className="flex items-center space-x-3">
              {user.email === 'kieranbond97@gmail.com' && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => navigate('/admin')}
                  className="flex items-center space-x-2 text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Shield className="w-4 h-4" />
                  <span className="hidden md:inline">Admin</span>
                </Button>
              )}
              <span className="text-sm text-gray-600">{user.email}</span>
              <Button variant="outline" size="sm" onClick={handleSignOut}>
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};
